/* StreamCommand — vanilla JS (no jQuery dependency)
   Handles: theme select sync, voting, ban form toggle
   timeago handled by Stimulus (stimulus_app.js)
   ======================================================== */
import { animate, cubicBezier } from "animejs";

// ---- Theme picker ----
function resolveTheme(stored) {
  if (stored === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return stored || "system";
}

function setThemeCookie(resolved) {
  document.cookie = "theme=" + resolved + "; path=/; max-age=31536000; samesite=lax";
}

function applyTheme(stored) {
  localStorage.setItem("theme", stored);
  var resolved = resolveTheme(stored);
  document.documentElement.setAttribute("data-theme", resolved);
  setThemeCookie(resolved);
  syncThemeLabel();
}

document.addEventListener("click", function (e) {
  var btn = e.target.closest("[data-set-theme]");
  if (!btn) return;
  applyTheme(btn.dataset.setTheme);
  var details = btn.closest("details");
  if (details) details.removeAttribute("open");
});

// Re-resolve system theme when OS preference changes.
window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", function () {
  if (localStorage.getItem("theme") === "system") {
    var resolved = resolveTheme("system");
    document.documentElement.setAttribute("data-theme", resolved);
    setThemeCookie(resolved);
  }
});

function syncThemeLabel() {
  var stored = localStorage.getItem("theme") || "system";
  var label = document.getElementById("theme-picker-label");
  if (label) label.textContent = stored.charAt(0).toUpperCase() + stored.slice(1);
}

document.addEventListener("turbo:load", syncThemeLabel);
document.addEventListener("DOMContentLoaded", syncThemeLabel);

// ---- Suggestion sorting (FLIP animation) ----
// Vote buttons are plain Turbo-intercepted links (data-turbo-stream) now —
// VotesController renders the badge/button/cluster-total updates directly,
// and turbo:before-stream-render below re-sorts once those streams land.
var _pendingAnimations = 0;

function _setSorting(active) {
  document.body.classList.toggle("vote-sorting", active);
}

function _animateElement(el, dx, dy) {
  _pendingAnimations++;
  _setSorting(true);
  animate(el, {
    translateX: [dx, 0],
    translateY: [dy, 0],
    duration: 350,
    ease: cubicBezier(0.25, 0.46, 0.45, 0.94),
    onComplete: function () {
      el.style.transform = "";
      _pendingAnimations--;
      if (_pendingAnimations <= 0) {
        _pendingAnimations = 0;
        _setSorting(false);
      }
    },
  });
}

function getVoteCount(el) {
  var badge = el.querySelector(".sc-vote-badge");
  if (badge) return parseInt(badge.textContent.trim(), 10) || 0;
  if (el.dataset.votes !== undefined) return parseInt(el.dataset.votes, 10) || 0;
  return 0;
}

function flipSort(items, parent) {
  if (items.length < 2) return;

  // First: record all positions before any DOM changes
  var firstRects = new Map();
  items.forEach(function (el) {
    firstRects.set(el, el.getBoundingClientRect());
  });

  var sorted = items.slice().sort(function (a, b) {
    return getVoteCount(b) - getVoteCount(a);
  });
  if (
    sorted.every(function (el, i) {
      return el === items[i];
    })
  )
    return;

  sorted.forEach(function (el) {
    parent.appendChild(el);
  });

  // Last: measure ALL new positions before applying any transforms
  var lastRects = new Map();
  sorted.forEach(function (el) {
    lastRects.set(el, el.getBoundingClientRect());
  });

  sorted.forEach(function (el) {
    var dx = firstRects.get(el).left - lastRects.get(el).left;
    var dy = firstRects.get(el).top - lastRects.get(el).top;
    if (dx !== 0 || dy !== 0) _animateElement(el, dx, dy);
  });
}

function sortClusterView(tbody) {
  var allRows = Array.from(tbody.children);
  if (allRows.length < 2) return;

  // Group rows into sortable units
  var units = [];
  var clusterIndex = {};

  allRows.forEach(function (row) {
    if (row.classList.contains("cluster-header")) {
      var totalEl = row.querySelector('[id^="cluster_total_"]');
      var total = parseInt(totalEl ? totalEl.textContent.trim() : "0", 10) || 0;
      var cid = row.id.replace("cluster-", "");
      clusterIndex[cid] = units.length;
      units.push({ rows: [row], totalVotes: total, clusterId: cid });
    } else if (row.classList.contains("cluster-child")) {
      var idx = clusterIndex[row.dataset.clusterId];
      if (idx !== undefined) units[idx].rows.push(row);
    } else {
      units.push({ rows: [row], totalVotes: getVoteCount(row), clusterId: null });
    }
  });

  // Record first positions for visible rows
  var firstRects = new Map();
  allRows.forEach(function (row) {
    if (row.style.display !== "none") firstRects.set(row, row.getBoundingClientRect());
  });

  // Sort units by total votes; sort children within each cluster by individual votes
  var sortedUnits = units.slice().sort(function (a, b) {
    return b.totalVotes - a.totalVotes;
  });
  sortedUnits.forEach(function (unit) {
    if (!unit.clusterId || unit.rows.length <= 1) return;
    var header = unit.rows[0];
    var children = unit.rows.slice(1).sort(function (a, b) {
      return getVoteCount(b) - getVoteCount(a);
    });
    unit.rows = [header].concat(children);
  });

  var newOrder = [];
  sortedUnits.forEach(function (u) {
    u.rows.forEach(function (r) {
      newOrder.push(r);
    });
  });
  if (
    newOrder.every(function (row, i) {
      return row === allRows[i];
    })
  )
    return;

  newOrder.forEach(function (row) {
    tbody.appendChild(row);
  });

  // Measure ALL new positions before animating
  var lastRects = new Map();
  newOrder.forEach(function (row) {
    if (row.style.display !== "none") lastRects.set(row, row.getBoundingClientRect());
  });

  newOrder.forEach(function (row) {
    if (!firstRects.has(row) || !lastRects.has(row)) return;
    var dy = firstRects.get(row).top - lastRects.get(row).top;
    if (dy !== 0) _animateElement(row, 0, dy);
  });
}

function sortAllSuggestions() {
  document.querySelectorAll('[data-vote-sort="table"]').forEach(function (tbody) {
    flipSort(Array.from(tbody.querySelectorAll('tr[id^="suggestion_"]')), tbody);
  });
  document.querySelectorAll('[data-vote-sort="bubbles"]').forEach(function (grid) {
    var cols = Array.from(grid.children).filter(function (el) {
      return el.dataset.suggestionId;
    });
    flipSort(cols, grid);
  });
  document.querySelectorAll('[data-vote-sort="clusters"]').forEach(function (tbody) {
    sortClusterView(tbody);
  });
  document.querySelectorAll('[data-vote-sort="hacker"]').forEach(function (body) {
    flipSort(Array.from(body.children), body);
  });
}

// Re-sort after Turbo Stream updates vote counts from other users
document.addEventListener("turbo:before-stream-render", function (evt) {
  var target = (evt.detail.newStream && evt.detail.newStream.getAttribute("target")) || "";
  if (
    target.startsWith("suggestion_votes_") ||
    target.startsWith("cluster_total_") ||
    target.startsWith("hacker_suggestion_")
  ) {
    setTimeout(sortAllSuggestions, 0);
  }
});

// ---- Cluster collapse ----
document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll("tr.cluster-header").forEach(function (row) {
    row.style.cursor = "pointer";
    row.title = "Click to expand";
  });
});

document.addEventListener("click", function (e) {
  var row = e.target.closest("tr.cluster-header");
  if (!row) return;
  if (e.target.closest(".vote-btn")) return; // vote clicks handled separately

  var clusterId = row.id.replace("cluster-", "");
  var children = document.querySelectorAll('tr.cluster-child[data-cluster-id="' + clusterId + '"]');
  if (!children.length) return;

  var collapsed = row.dataset.collapsed === "true";
  children.forEach(function (child) {
    child.style.display = collapsed ? "" : "none";
  });
  row.dataset.collapsed = collapsed ? "false" : "true";
  row.title = collapsed ? "Click to collapse" : "Click to expand";
});

// ---- Ban form toggle ----
document.addEventListener("click", function (e) {
  var toggler = e.target.closest("[data-target]");
  if (!toggler || !toggler.dataset.target || !toggler.dataset.action?.includes("ban-form")) return;
  e.preventDefault();

  var formEl = document.getElementById(toggler.dataset.target);
  if (formEl) {
    formEl.style.display = formEl.style.display === "none" ? "block" : "none";
  }
});

// ---- Dev test notification button ----
(function () {
  function csrfToken() {
    return (document.querySelector('meta[name="csrf-token"]') || {}).content || "";
  }

  function initDevNotificationButton() {
    document.querySelectorAll("[data-dev-notification]").forEach(function (btn) {
      if (btn.dataset.devNotificationInit) return;
      btn.dataset.devNotificationInit = "1";
      btn.addEventListener("click", function () {
        var select = document.getElementById("dev-notification-kind");
        var kind = select ? select.value : "info";
        fetch("/dev/notification?kind=" + kind, {
          headers: { "X-CSRF-Token": csrfToken(), Accept: "application/json" },
        });
      });
    });
  }

  document.addEventListener("turbo:load", initDevNotificationButton);
  document.addEventListener("DOMContentLoaded", initDevNotificationButton);
})();

// ---- Client-side toast helper ----
(function () {
  window.StreamCommand = window.StreamCommand || {};
  var TOAST_ICONS = { error: "fa-circle-xmark", success: "fa-circle-check" };
  var TOAST_COLORS = { error: "alert-error", success: "alert-success" };

  StreamCommand.toast = function (message, type) {
    var container = document.getElementById("flash-container");
    if (!container) return;

    var div = document.createElement("div");
    div.className =
      "alert " +
      (TOAST_COLORS[type] || "alert-info") +
      " sc-toast pointer-events-auto w-full max-w-sm";
    div.setAttribute("data-controller", "flash");

    var icon = document.createElement("i");
    icon.className = "fa-solid " + (TOAST_ICONS[type] || "fa-circle-info");

    var span = document.createElement("span");
    span.textContent = message;

    div.appendChild(icon);
    div.appendChild(span);
    container.appendChild(div);
  };
})();

// ---- Turbo confirm — themed DaisyUI dialog ----
(function () {
  function installConfirmMethod() {
    if (!window.Turbo) return;
    Turbo.config.confirmMethod = function (message, element, submitter) {
      return new Promise(function (resolve) {
        var dialog = document.getElementById("sc-turbo-confirm");
        var msgEl = document.getElementById("sc-turbo-confirm-message");
        var okBtn = document.getElementById("sc-turbo-confirm-ok");
        var cancelBtn = document.getElementById("sc-turbo-confirm-cancel");
        if (!dialog) {
          resolve(false);
          return;
        }

        if (msgEl) msgEl.textContent = message;

        var trigger = submitter || element;
        var variant = trigger && trigger.dataset && trigger.dataset.confirmVariant;
        var btnClass =
          variant === "primary"
            ? "btn-primary"
            : variant === "warning"
              ? "btn-warning"
              : "btn-error";
        okBtn.className = "btn btn-sm " + btnClass;

        function cleanup() {
          okBtn.removeEventListener("click", onOk);
          cancelBtn.removeEventListener("click", onCancel);
          dialog.close();
        }
        function onOk() {
          cleanup();
          resolve(true);
        }
        function onCancel() {
          cleanup();
          resolve(false);
        }

        okBtn.addEventListener("click", onOk);
        cancelBtn.addEventListener("click", onCancel);
        dialog.showModal();
      });
    };
  }

  document.addEventListener("turbo:load", installConfirmMethod);
  document.addEventListener("DOMContentLoaded", installConfirmMethod);
})();
